import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
}

function CheckIcon() {
  return (
    <svg
      className="w-4 h-4 text-accent-primary flex-shrink-0 mt-[2px]"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  )
}

function PlanCard({ plan, isAnnual }) {
  const monthlyDisplay = isAnnual ? plan.price_annual_monthly : plan.price_monthly
  const annualTotal = new Intl.NumberFormat('pt-BR').format(plan.price_annual_total)
  const ctaText = `Começar com ${plan.name}`

  return (
    <motion.div
      className={`relative flex flex-col bg-surface-card rounded-card p-6 overflow-hidden ${
        plan.highlighted
          ? 'border-[3px] border-accent-primary shadow-xl'
          : 'border border-border-default shadow-card'
      }`}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-100px' }}
    >
      <div className="relative z-10 flex flex-col h-full">
        <h3 className="text-[20px] font-bold text-text-primary mb-1">{plan.name}</h3>
        <p className="text-[13px] text-text-muted mb-5 pr-12">{plan.description}</p>

        <div className="flex items-end gap-1 mb-5">
          <span className="text-[32px] md:text-[34px] font-extrabold text-accent-primary leading-none transition-all duration-300">
            R$ {monthlyDisplay}
          </span>
          <span className="text-[14px] font-medium text-text-secondary mb-1">/mês</span>
        </div>

        {isAnnual && (
          <p className="text-[12px] text-[#34C759] font-medium mb-3 -mt-4">
            Cobrado R$ {annualTotal} anualmente
          </p>
        )}

        <a
          href="#"
          aria-label={`${ctaText} — abre formulário de cadastro`}
          onClick={(event) => event.preventDefault()}
          className={`${plan.highlighted ? 'btn-primary' : 'btn-ghost'} w-full mb-5 !py-2.5 !text-[14px]`}
        >
          {ctaText}
        </a>

        <div className="flex flex-col gap-3">
          {plan.features.map((feat, idx) => (
            <div key={idx} className="flex items-start gap-2.5">
              <CheckIcon />
              <span
                className={`text-[14px] ${
                  plan.highlighted ? 'text-text-primary font-medium' : 'text-text-secondary'
                }`}
              >
                {feat}
              </span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  )
}

export default function Pricing() {
  const [isAnnual, setIsAnnual] = useState(true)
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('/api/v1/plans')
      .then((r) => r.json())
      .then((data) => setPlans(data))
      .finally(() => setLoading(false))
  }, [])

  return (
    <section id="planos" className="w-full bg-surface-section-alt py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-6 w-full flex flex-col items-center">
        <div className="text-center w-full max-w-[880px] mx-auto mb-6">
          <span className="badge-info mb-4 text-[11px] inline-flex mx-auto">PLANOS</span>
          <h2 className="text-[26px] md:text-[32px] font-bold text-text-primary tracking-tight">
            Consultoria jurídica no bolso.
            <br className="hidden md:block" />
            <span className="text-accent-primary">Por menos que 1 consulta.</span>
          </h2>
          <p className="mt-3 text-[15px] md:text-[16px] text-text-secondary max-w-[620px] mx-auto">
            Escolha o plano que faz sentido para o seu momento de carreira.
          </p>
        </div>

        <div className="flex items-center justify-center mb-8">
          <div className="relative flex items-center bg-gray-200/50 p-1 rounded-full">
            <button
              onClick={() => setIsAnnual(false)}
              aria-label="Ver preços mensais"
              className={`relative px-5 py-2 text-[13px] font-bold rounded-full transition-colors duration-300 ${
                !isAnnual ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {!isAnnual && (
                <motion.div
                  layoutId="pricing-toggle-pill"
                  className="absolute inset-0 bg-white rounded-full shadow-sm"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <span className="relative z-10">Mensal</span>
            </button>

            <button
              onClick={() => setIsAnnual(true)}
              aria-label="Ver preços anuais com 20% de desconto"
              className={`relative flex items-center gap-2 px-5 py-2 text-[13px] font-bold rounded-full transition-colors duration-300 ${
                isAnnual ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {isAnnual && (
                <motion.div
                  layoutId="pricing-toggle-pill"
                  className="absolute inset-0 bg-white rounded-full shadow-sm"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <span className="relative z-10 flex items-center gap-2">
                Anual
                <span className="bg-[#34C759]/10 text-[#34C759] text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full">
                  -20%
                </span>
              </span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col md:flex-row flex-wrap justify-center gap-6 w-full max-w-[1120px] mx-auto">
            {[0, 1, 2].map((i) => (
              <div key={i} className="w-full md:w-[340px] h-80 bg-surface-card rounded-card border border-border-default animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="flex flex-col md:flex-row flex-wrap justify-center gap-6 w-full max-w-[1120px] mx-auto">
            {plans.map((plan) => (
              <div key={plan.id} className="w-full md:w-[340px]">
                <PlanCard plan={plan} isAnnual={isAnnual} />
              </div>
            ))}
          </div>
        )}

        <p className="text-center text-sm text-gray-500 mt-8">
          O plano Especialista inclui 1 especialidade. Especialidades adicionais
          por +40% do valor do plano.
        </p>
      </div>
    </section>
  )
}
